import {
  Chart as ChartJS,
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useMemo } from "react";

ChartJS.register(
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);
export const OverviewChart = ({ jobs = [], applications = [] }) => {
  // Dynamically calculate the last 6 months of data
  const { months, appCounts, jobCounts, totalApps, growthText, isPositive } =
    useMemo(() => {
      const monthLabels = [];
      const appsPerMonth = [];
      const jobsPerMonth = [];

      // Loop backwards to get the last 6 months including current month
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        monthLabels.push(d.toLocaleString("default", { month: "short" }));

        const monthIndex = d.getMonth();
        const year = d.getFullYear();

        // Count applications for this specific month
        const apps = applications.filter((a) => {
          const ad = new Date(a.createdDate);
          return ad.getMonth() === monthIndex && ad.getFullYear() === year;
        }).length;
        appsPerMonth.push(apps);

        // Count jobs posted for this specific month
        const jbs = jobs.filter((j) => {
          const jd = new Date(j.postedOn);
          return jd.getMonth() === monthIndex && jd.getFullYear() === year;
        }).length;
        jobsPerMonth.push(jbs);
      }

      // Calculate Growth (Current Month vs Last Month)
      const currentMonthApps = appsPerMonth[5];
      const lastMonthApps = appsPerMonth[4];
      let growth = 0;
      if (lastMonthApps > 0) {
        growth = ((currentMonthApps - lastMonthApps) / lastMonthApps) * 100;
      }

      return {
        months: monthLabels,
        appCounts: appsPerMonth,
        jobCounts: jobsPerMonth,
        totalApps: applications.length,
        growthText: `${Math.abs(growth).toFixed(1)}% last month`,
        isPositive: growth >= 0,
      };
    }, [jobs, applications]);

  const data = {
    labels: months,
    datasets: [
      {
        type: "line",
        label: "Applicants",
        data: appCounts,
        borderColor: "#3B5BDB",
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: "white",
        pointBorderColor: "#3B5BDB",
        pointBorderWidth: 2,
        pointRadius: 4,
        order: 1,
      },
      {
        type: "bar",
        label: "Jobs Posted",
        data: jobCounts,
        backgroundColor: (context) =>
          context.dataIndex === 5 ? "#3B5BDB" : "#F1F3F5", // Highlight current month
        borderRadius: 8,
        barThickness: 32,
        order: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, border: { display: false } },
      y: { display: false, grid: { display: false }, min: 0 },
    },
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <h3 style={styles.title}>Overviews</h3>
      </div>

      <div style={styles.stats}>
        <h1 style={styles.bigNumber}>{totalApps.toLocaleString()}</h1>
        <span
          style={{
            ...styles.growth,
            color: isPositive ? "#2ECA7F" : "#DC3545",
          }}
        >
          {isPositive ? "▲" : "▼"} {growthText}
        </span>
      </div>

      <div style={styles.chartContainer}>
        <Chart type="line" data={data} options={options} />
      </div>
    </div>
  );
};

const styles = {
  wrapper: { width: "100%", fontFamily: "sans-serif" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { margin: 0, fontSize: "18px", color: "#333" },
  stats: {
    display: "flex",
    alignItems: "baseline",
    gap: "12px",
    margin: "16px 0 24px 0",
  },
  bigNumber: { margin: 0, fontSize: "36px", color: "#111" },
  growth: { margin: 0, fontSize: "14px", fontWeight: "bold" },
  chartContainer: { position: "relative", height: "250px", width: "100%", overflow: "hidden" },
};
